import React from "react";
import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenu,
  NavbarMenuItem,
  NavbarMenuToggle,
  Button,
  Link,
} from "@heroui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useUploadModal } from "../contexts/UploadModalContext";
import { useTheme } from "../themes";
import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { SunIcon, MoonIcon } from "@heroicons/react/24/solid";
import { Icon } from "@iconify/react";
import clipkiteLogoLight from "../images/clipkiteLight.png";
import clipkiteLogoDark from "../images/clipkiteDark.png";
import LoginModal from "./LoginModal";
import SignUpModal from "./SignUpModal";
import FeedbackModal from "./FeedbackModal";
import UserMenu from "./UserMenu";


const CenteredHeroNav = ({ triggerLogin }) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isLoginOpen, setIsLoginOpen] = React.useState(false);
  const [isSignUpOpen, setIsSignUpOpen] = React.useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = React.useState(false);
  const { currentUser } = useAuth();
  const { openUploadModal } = useUploadModal();
  const { currentMode, toggleMode } = useTheme();
  const location = useLocation();

  // Watch for triggerLogin prop changes
  React.useEffect(() => {
    if (triggerLogin) {
      setIsLoginOpen(true);
    }
  }, [triggerLogin]);

  const handleSwitchToSignUp = () => {
    setIsLoginOpen(false);
    setIsSignUpOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignUpOpen(false);
    setIsLoginOpen(true);
  };

  // Detect mobile
  const isMobile =
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
    ('ontouchstart' in window) ||
    (window.innerWidth <= 768);

  // On mobile, if any modal is open, show only that modal (hide navbar)
  if (isMobile && (isLoginOpen || isSignUpOpen || isFeedbackOpen)) {
    return (
      <>
        {isLoginOpen && (
          <LoginModal
            isOpen={isLoginOpen}
            onClose={() => {
              setIsLoginOpen(false);
              setIsMenuOpen(false); // Close hamburger menu
            }}
            onSwitchToSignUp={handleSwitchToSignUp}
          />
        )}
        {isSignUpOpen && (
          <SignUpModal
            isOpen={isSignUpOpen}
            onClose={() => {
              setIsSignUpOpen(false);
              setIsMenuOpen(false); // Close hamburger menu
            }}
            onSwitchToLogin={handleSwitchToLogin}
          />
        )}
        {isFeedbackOpen && (
          <FeedbackModal
            isOpen={isFeedbackOpen}
            onClose={() => {
              setIsFeedbackOpen(false);
              setIsMenuOpen(false); // Close hamburger menu
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
    <Navbar
      classNames={{
        base: "bg-white/80 dark:bg-slate-900/90 backdrop-blur-md border-b border-gray-200/50 dark:border-slate-700/50",
        item: "data-[active=true]:text-primary data-[active=true]:font-semibold",
        wrapper: "px-6 sm:px-8 max-w-7xl py-2",
      }}
      height="90px"
      maxWidth="full"
      isMenuOpen={isMenuOpen}
      onMenuOpenChange={setIsMenuOpen}
    >
      {/* Left: Logo */}
      <NavbarBrand className="gap-0" style={{ padding: 0 }}>
        <NavbarMenuToggle className="mr-3 h-6 sm:hidden" />
        <RouterLink to="/" className="flex items-center" style={{ textDecoration: "none", height: "100%" }}>
          <img
            src={currentMode === 'dark' ? clipkiteLogoDark : clipkiteLogoLight}
            alt="ClipKite"
            style={{ height: "90px", width: "auto" }}
            className="transition-transform hover:scale-105"
          />
        </RouterLink>
      </NavbarBrand>

      {/* Center: Navigation Links */}
      <NavbarContent
        className="bg-gray-100/80 dark:bg-slate-800/60 backdrop-blur-sm hidden h-11 max-w-fit gap-1 rounded-full px-2 sm:flex"
        justify="center"
      >
        <NavbarItem isActive={location.pathname === "/"}>
          <Link
            as={RouterLink}
            to="/"
            className="px-4 py-2 rounded-full text-sm font-medium transition-colors hover:bg-white/80 dark:hover:bg-slate-700/80 dark:text-slate-200"
            aria-current={location.pathname === "/" ? "page" : undefined}
          >
            Home
          </Link>
        </NavbarItem>
        <NavbarItem isActive={location.pathname === "/locations"}>
          <Link
            as={RouterLink}
            to="/locations"
            className="px-4 py-2 rounded-full text-sm font-medium transition-colors hover:bg-white/80 dark:hover:bg-slate-700/80 dark:text-slate-200"
            aria-current={location.pathname === "/locations" ? "page" : undefined}
          >
            Locations
          </Link>
        </NavbarItem>
        <NavbarItem isActive={location.pathname === "/terms"}>
          <Link
            as={RouterLink}
            to="/terms"
            className="px-4 py-2 rounded-full text-sm font-medium transition-colors hover:bg-white/80 dark:hover:bg-slate-700/80 dark:text-slate-200"
            aria-current={location.pathname === "/terms" ? "page" : undefined}
          >
            Terms
          </Link>
        </NavbarItem>
        <NavbarItem isActive={location.pathname === "/privacy-policy"}>
          <Link
            as={RouterLink}
            to="/privacy-policy"
            className="px-4 py-2 rounded-full text-sm font-medium transition-colors hover:bg-white/80 dark:hover:bg-slate-700/80 dark:text-slate-200"
            aria-current={location.pathname === "/privacy-policy" ? "page" : undefined}
          >
            Privacy
          </Link>
        </NavbarItem>
      </NavbarContent>

      {/* Right: Upload & User */}
      <NavbarContent
        className="flex h-11 max-w-fit items-center gap-3"
        justify="end"
      >
        {!currentUser ? (
          <>
            <NavbarItem>
              <Button
                radius="full"
                variant="flat"
                size="md"
                className="font-medium"
                onPress={() => setIsLoginOpen(true)}
              >
                Login
              </Button>
            </NavbarItem>
            {/* Mobile Theme Toggle - next to Login with more spacing */}
            <NavbarItem className="flex sm:hidden ml-4">
              <Button
                isIconOnly
                radius="full"
                variant="light"
                onPress={toggleMode}
                className="hover:bg-gray-100 dark:hover:bg-slate-800"
              >
                {currentMode === 'dark' ? (
                  <SunIcon className="w-5 h-5 text-yellow-500" />
                ) : (
                  <MoonIcon className="w-5 h-5 text-gray-600 dark:text-slate-300" />
                )}
              </Button>
            </NavbarItem>
          </>
        ) : (
          <>
            <NavbarItem className="hidden sm:flex">
              <Button
                isIconOnly
                radius="full"
                variant="light"
                onPress={openUploadModal}
                className="hover:bg-gray-100 dark:hover:bg-slate-800"
              >
                <ArrowUpTrayIcon className="w-5 h-5 text-gray-600 dark:text-slate-300" />
              </Button>
            </NavbarItem>
            <NavbarItem>
              <UserMenu />
            </NavbarItem>
            {/* Mobile Theme Toggle - next to UserMenu with more spacing */}
            <NavbarItem className="flex sm:hidden ml-4">
              <Button
                isIconOnly
                radius="full"
                variant="light"
                onPress={toggleMode}
                className="hover:bg-gray-100 dark:hover:bg-slate-800"
              >
                {currentMode === 'dark' ? (
                  <SunIcon className="w-5 h-5 text-yellow-500" />
                ) : (
                  <MoonIcon className="w-5 h-5 text-gray-600 dark:text-slate-300" />
                )}
              </Button>
            </NavbarItem>
          </>
        )}
      </NavbarContent>

      {/* Far Right: Feedback & Theme Toggle - Always visible, separated */}
      <NavbarContent
        className="ml-4 flex h-11 max-w-fit items-center gap-2"
        justify="end"
      >
        <NavbarItem className="hidden sm:flex">
          <Button
            radius="full"
            variant="flat"
            size="md"
            className="font-medium"
            onPress={() => setIsFeedbackOpen(true)}
          >
            Feedback
          </Button>
        </NavbarItem>
        <NavbarItem className="hidden sm:flex">
          <Button
            isIconOnly
            radius="full"
            variant="light"
            onPress={toggleMode}
            className="hover:bg-gray-100 dark:hover:bg-slate-800"
          >
            {currentMode === 'dark' ? (
              <SunIcon className="w-5 h-5 text-yellow-500" />
            ) : (
              <MoonIcon className="w-5 h-5 text-gray-600 dark:text-slate-300" />
            )}
          </Button>
        </NavbarItem>
      </NavbarContent>

      <NavbarMenu
        className="bg-white dark:bg-slate-900 shadow-medium top-[calc(var(--navbar-height)-1px)] max-h-[70vh] pt-6 backdrop-blur-xl backdrop-saturate-150 border-t border-default-100/10 dark:border-slate-700/50"
        motionProps={{
          initial: {opacity: 0, y: -20},
          animate: {opacity: 1, y: 0},
          exit: {opacity: 0, y: -20},
          transition: {
            ease: "easeInOut",
            duration: 0.2,
          },
        }}
      >
        <NavbarMenuItem>
          <RouterLink to="/" className="w-full text-gray-800 dark:text-slate-200">Home</RouterLink>
        </NavbarMenuItem>
        <NavbarMenuItem>
          <RouterLink to="/locations" className="w-full text-gray-800 dark:text-slate-200">Locations</RouterLink>
        </NavbarMenuItem>
        <NavbarMenuItem>
          <RouterLink to="/terms" className="w-full text-gray-800 dark:text-slate-200">Terms</RouterLink>
        </NavbarMenuItem>
        <NavbarMenuItem>
          <RouterLink to="/privacy-policy" className="w-full text-gray-800 dark:text-slate-200">Privacy</RouterLink>
        </NavbarMenuItem>
        {/* Mobile menu actions */}
        <NavbarMenuItem>
          <Button
            fullWidth
            variant="flat"
            size="md"
            onPress={() => setIsFeedbackOpen(true)}
            startContent={<Icon icon="solar:chat-round-line-bold-duotone" width={20} />}
          >
            Feedback
          </Button>
        </NavbarMenuItem>
        {!currentUser ? (
          <NavbarMenuItem>
            <Button
              fullWidth
              color="primary"
              variant="flat"
              size="md"
              onPress={() => setIsLoginOpen(true)}
            >
              Login
            </Button>
          </NavbarMenuItem>
        ) : (
          <NavbarMenuItem>
            <Button
              fullWidth
              color="primary"
              variant="flat"
              size="md"
              onPress={openUploadModal}
              startContent={<ArrowUpTrayIcon className="w-5 h-5" />}
            >
              Upload
            </Button>
          </NavbarMenuItem>
        )}
      </NavbarMenu>
    </Navbar>

    {/* Modals */}
    <LoginModal
      isOpen={isLoginOpen}
      onClose={() => setIsLoginOpen(false)}
      onSwitchToSignUp={handleSwitchToSignUp}
    />
    <SignUpModal
      isOpen={isSignUpOpen}
      onClose={() => setIsSignUpOpen(false)}
      onSwitchToLogin={handleSwitchToLogin}
    />
    <FeedbackModal
      isOpen={isFeedbackOpen}
      onClose={() => setIsFeedbackOpen(false)}
    />
    </>
  );
};

export default CenteredHeroNav;
